import { PAYMENT_CONFIG, PICKUP_CONFIG } from "@/constants/status";
import type { MenuProduct, PaymentMethod, PickupMethod } from "@/types";
import { formatSAR } from "@/utils/format";

export interface CartLine {
  product: MenuProduct;
  quantity: number;
}

interface PosCartProps {
  lines: CartLine[];
  subtotal: number;
  discount: number;
  vat: number;
  total: number;
  payment: PaymentMethod;
  pickup: PickupMethod;
  pickupMethods: PickupMethod[];
  onInc: (productId: string) => void;
  onDec: (productId: string) => void;
  onRemove: (productId: string) => void;
  onClear: () => void;
  onDiscountChange: (value: string) => void;
  onPaymentChange: (value: PaymentMethod) => void;
  onPickupChange: (value: PickupMethod) => void;
  onCheckout: () => void;
}

const PAYMENT_METHODS: PaymentMethod[] = ["cash", "card", "applepay", "wallet"];

export default function PosCart({
  lines,
  subtotal,
  discount,
  vat,
  total,
  payment,
  pickup,
  pickupMethods,
  onInc,
  onDec,
  onRemove,
  onClear,
  onDiscountChange,
  onPaymentChange,
  onPickupChange,
  onCheckout,
}: PosCartProps) {
  return (
    <div className="bg-white rounded-card border border-background-200 shadow-card flex flex-col xl:h-[calc(100vh-160px)] xl:sticky xl:top-20">
      <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-shopping-basket-2-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">
            الطلب الحالي
          </h2>
        </div>
        {lines.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-500 hover:bg-red-100 hover:text-red-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-6-line"></i>
            تفريغ
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {lines.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-3">
              <i className="ri-shopping-basket-2-line text-2xl"></i>
            </div>
            <p className="text-sm text-foreground-500">
              اضغط على أي منتج لإضافته للطلب
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {lines.map((line) => (
              <li
                key={line.product.id}
                className="flex items-center gap-3 rounded-btn border border-background-200 p-2.5"
              >
                <img
                  src={line.product.image}
                  alt={line.product.name}
                  className="w-11 h-11 rounded-btn object-cover object-top shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900 line-clamp-1">
                    {line.product.name}
                  </p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {formatSAR(line.product.price)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => onDec(line.product.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                    aria-label="تقليل"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-6 text-center text-sm font-bold text-foreground-950">
                    {line.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => onInc(line.product.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                    aria-label="زيادة"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(line.product.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:bg-red-100 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                    aria-label="حذف"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-background-100 px-5 py-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-600 whitespace-nowrap">
            خصم
          </span>
          <div className="flex-1 flex items-center rounded-btn border border-background-200 bg-white px-2 focus-within:ring-2 focus-within:ring-primary-300">
            <input
              type="number"
              min="0"
              step="0.5"
              value={discount === 0 ? "" : discount}
              onChange={(event) => onDiscountChange(event.target.value)}
              placeholder="0"
              className="flex-1 h-9 bg-transparent px-1 text-sm outline-none"
            />
            <span className="text-xs text-foreground-400">ر.س</span>
          </div>
        </div>

        <div className="space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-foreground-500">المجموع</span>
            <span className="text-foreground-800">{formatSAR(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-foreground-500">الخصم</span>
              <span className="text-foreground-800">
                - {formatSAR(discount)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-foreground-500">الضريبة المتضمنة (15%)</span>
            <span className="text-foreground-800">{formatSAR(vat)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-dashed border-background-200 pt-2">
            <span className="font-bold text-foreground-950">الإجمالي</span>
            <span className="text-lg font-bold text-primary-700">
              {formatSAR(total)}
            </span>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-foreground-500 mb-1.5">
            طريقة الاستلام
          </p>
          <div className="space-y-1.5">
            {pickupMethods.map((method) => {
              const config = PICKUP_CONFIG[method];
              const active = pickup === method;
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => onPickupChange(method)}
                  className={`w-full flex items-center gap-2 rounded-btn px-3 py-2 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center text-base">
                    <i className={config.icon}></i>
                  </span>
                  {config.label}
                  {active && <i className="ri-check-line ms-auto"></i>}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-foreground-500 mb-1.5">
            طريقة الدفع
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {PAYMENT_METHODS.map((method) => {
              const config = PAYMENT_CONFIG[method];
              const active = payment === method;
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => onPaymentChange(method)}
                  className={`flex flex-col items-center gap-1 rounded-btn py-2 text-[11px] font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center text-base">
                    <i className={config.icon}></i>
                  </span>
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          disabled={lines.length === 0}
          onClick={onCheckout}
          className="w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-lg"></i>
            إتمام البيع وإصدار الفاتورة
          </span>
        </button>
      </div>
    </div>
  );
}